<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';

import { messageStamp, dynamicTime } from 'shared/helpers/timeHelper';

import Pagination from 'dashboard/components/table/Pagination.vue';
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName.vue';
import ShowMore from 'dashboard/components/widgets/ShowMore.vue';
import CsatContactCell from './CsatContactCell.vue';
import CsatExpandedRow from './CsatExpandedRow.vue';
import CsatEmptyState from './CsatEmptyState.vue';
import CsatTableLoader from './CsatTableLoader.vue';
import ReportSectionCard from './ReportSectionCard.vue';

import { CSAT_RATINGS } from 'shared/constants/messages';

import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';

const { pageIndex } = defineProps({
  pageIndex: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['pageChange']);
const { t } = useI18n();
const { isCloudFeatureEnabled, isOnTalkviaCloud } = useAccount();
const csatResponses = useMapGetter('csat/getCSATResponses');

const isFeatureEnabled = computed(() =>
  isCloudFeatureEnabled('csat_review_notes')
);
const showExpandableRows = computed(
  () => isFeatureEnabled.value || isOnTalkviaCloud.value
);
const metrics = useMapGetter('csat/getMetrics');
const uiFlags = useMapGetter('csat/getUIFlags');

const isLoading = computed(() => uiFlags.value.isFetching);

const expandedRows = ref({});

const toggleRow = id => {
  expandedRows.value = {
    ...expandedRows.value,
    [id]: !expandedRows.value[id],
  };
};

const isRowExpanded = id => !!expandedRows.value[id];

const tableData = computed(() => {
  return csatResponses.value.map(response => ({
    id: response.id,
    contact: response.contact,
    assignedAgent: response.assigned_agent,
    rating: response.rating,
    feedbackText: response.feedback_message || '',
    conversationId: response.conversation_id,
    csatReviewNotes: response.csat_review_notes,
    createdAgo: dynamicTime(response.created_at),
    createdAt: messageStamp(response.created_at, 'LLL d yyyy, h:mm a'),
    _original: response,
  }));
});

const tableDescription = computed(() =>
  showExpandableRows.value
    ? t('CSAT_REPORTS.TABLE.DESCRIPTION_EXPANDABLE')
    : t('CSAT_REPORTS.TABLE.DESCRIPTION')
);

const totalResponsesLabel = computed(() =>
  t('CSAT_REPORTS.TABLE.TOTAL', {
    count: (metrics.value.totalResponseCount || 0).toLocaleString(),
  })
);

const columnCount = computed(() => (showExpandableRows.value ? 5 : 4));

const getRatingData = rating => {
  return CSAT_RATINGS.find(r => r.value === rating) || {};
};

const columnHelper = createColumnHelper();

const columns = computed(() => {
  const baseColumns = [
    columnHelper.accessor('contact', {
      header: t('CSAT_REPORTS.TABLE.HEADER.CONTACT_NAME'),
    }),
    columnHelper.accessor('rating', {
      header: t('CSAT_REPORTS.TABLE.HEADER.RATING'),
      size: 120,
    }),
    columnHelper.accessor('feedbackText', {
      header: t('CSAT_REPORTS.TABLE.HEADER.FEEDBACK_TEXT'),
      size: 500,
    }),
    columnHelper.accessor('assignedAgent', {
      header: t('CSAT_REPORTS.TABLE.HEADER.HANDLED_BY'),
      size: 160,
    }),
  ];

  if (showExpandableRows.value) {
    baseColumns.push(
      columnHelper.accessor('actions', {
        header: '',
        size: 50,
      })
    );
  }

  return baseColumns;
});

const paginationParams = computed(() => {
  return {
    pageIndex: pageIndex,
    pageSize: 25,
  };
});

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return metrics.value.totalResponseCount;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagination = updater(paginationParams.value);
    emit('pageChange', newPagination.pageIndex);
  },
});
</script>

<template>
  <ReportSectionCard
    :title="$t('CSAT_REPORTS.TABLE.TITLE')"
    :description="tableDescription"
    icon="i-hugeicons-bubble-chat-question"
    flush
  >
    <template v-if="metrics.totalResponseCount" #actions>
      <span
        class="inline-flex items-center rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs font-medium tabular-nums text-n-slate-11"
      >
        {{ totalResponsesLabel }}
      </span>
    </template>

    <div class="border-t border-n-weak dark:border-n-strong">
      <CsatTableLoader v-if="isLoading" />

      <div v-else-if="tableData.length" class="overflow-x-auto">
        <table class="w-full min-w-[48rem] border-collapse">
          <thead class="bg-n-solid-2">
            <tr class="border-b border-n-weak dark:border-n-strong">
              <th
                v-for="header in table.getFlatHeaders()"
                :key="header.id"
                scope="col"
                :style="{
                  width: header.getSize() ? `${header.getSize()}px` : 'auto',
                }"
                class="px-5 py-2.5 text-left text-xs font-medium text-n-slate-11 rtl:text-right"
              >
                <span v-if="header.column.columnDef.header">
                  {{ header.column.columnDef.header }}
                </span>
                <span v-else class="sr-only">
                  {{ $t('CSAT_REPORTS.TABLE.TOGGLE_NOTES') }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in tableData" :key="row.id">
              <tr
                class="group border-b border-n-weak transition-colors last:border-b-0 hover:bg-n-alpha-1 dark:border-n-strong"
                :class="{
                  'bg-n-alpha-1': isRowExpanded(row.id),
                  'cursor-pointer': showExpandableRows,
                }"
                @click="showExpandableRows && toggleRow(row.id)"
              >
                <td class="px-5 py-3.5 align-top">
                  <CsatContactCell
                    :contact="row.contact"
                    :conversation-id="row.conversationId"
                    :created-ago="row.createdAgo"
                    :created-at="row.createdAt"
                  />
                </td>
                <td class="px-5 py-3.5 align-top">
                  <span
                    class="inline-flex max-w-full items-center gap-1.5 rounded-md border border-n-weak bg-n-solid-2 px-2 py-1 dark:border-n-strong"
                  >
                    <span
                      class="size-2 shrink-0 rounded-full"
                      :style="{
                        backgroundColor: getRatingData(row.rating).color,
                      }"
                      aria-hidden="true"
                    />
                    <span aria-hidden="true" class="text-sm leading-none">
                      {{ getRatingData(row.rating).emoji }}
                    </span>
                    <span class="truncate text-xs font-medium text-n-slate-12">
                      {{ $t(getRatingData(row.rating).translationKey) }}
                    </span>
                  </span>
                </td>
                <td class="px-5 py-3.5 align-top">
                  <span
                    v-if="!row.feedbackText"
                    class="text-sm italic text-n-slate-10"
                  >
                    {{ $t('CSAT_REPORTS.NO_FEEDBACK') }}
                  </span>
                  <div v-else class="text-sm text-n-slate-12">
                    <ShowMore :text="row.feedbackText" :limit="100" />
                  </div>
                </td>
                <td class="px-5 py-3.5 align-top">
                  <UserAvatarWithName
                    v-if="row.assignedAgent"
                    :user="row.assignedAgent"
                    :size="28"
                  />
                  <span v-else class="text-sm italic text-n-slate-10">
                    {{ $t('CSAT_REPORTS.NO_AGENT') }}
                  </span>
                </td>
                <td v-if="showExpandableRows" class="px-3 py-3.5 align-top">
                  <button
                    type="button"
                    class="grid size-7 place-items-center rounded-md text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand group-hover:text-n-slate-12"
                    :aria-expanded="isRowExpanded(row.id)"
                    :aria-label="$t('CSAT_REPORTS.TABLE.TOGGLE_NOTES')"
                    @click.stop="toggleRow(row.id)"
                  >
                    <span
                      class="block size-4"
                      :class="
                        isRowExpanded(row.id)
                          ? 'i-hugeicons-arrow-up-01'
                          : 'i-hugeicons-arrow-down-01'
                      "
                      aria-hidden="true"
                    />
                  </button>
                </td>
              </tr>
              <tr
                v-if="showExpandableRows && isRowExpanded(row.id)"
                class="border-b border-n-weak last:border-b-0 dark:border-n-strong"
              >
                <td :colspan="columnCount" class="p-0">
                  <CsatExpandedRow :response="row._original" />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <CsatEmptyState
        v-else
        :title="$t('CSAT_REPORTS.NO_RECORDS')"
        :description="$t('CSAT_REPORTS.NO_RECORDS_DESCRIPTION')"
      />
    </div>

    <div
      v-if="metrics.totalResponseCount"
      class="border-t border-n-weak px-5 py-3 dark:border-n-strong"
    >
      <Pagination :table="table" />
    </div>
  </ReportSectionCard>
</template>
